const OFFICIAL_YOUTUBE_HOSTS = new Set([
  "youtu.be",
  "youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
]);

const YOUTUBE_VIDEO_ID_MIN_LENGTH = 10;

const normalizeYouTubeHostname = (hostname = "") =>
  String(hostname || "").replace(/^www\./i, "").toLowerCase();

export const isOfficialYouTubeHost = (hostname = "") => {
  const host = normalizeYouTubeHostname(hostname);
  return (
    OFFICIAL_YOUTUBE_HOSTS.has(host)
    || host.endsWith(".youtube.com")
    || host.endsWith(".youtube-nocookie.com")
  );
};

export const isLikelyYouTubeVideoId = (videoId = "") => {
  const value = String(videoId || "").trim();
  return value.length >= YOUTUBE_VIDEO_ID_MIN_LENGTH && /^[A-Za-z0-9_-]+$/.test(value);
};

const parseUrlMaybeMissingProtocol = (rawUrl) => {
  try {
    return new URL(rawUrl);
  } catch {
    return new URL(`https://${rawUrl}`);
  }
};

export const parseYouTubeStartSeconds = (rawValue) => {
  if (rawValue == null) return null;

  const value = String(rawValue).trim();
  if (!value) return null;

  if (/^\d+$/.test(value)) {
    return Number(value);
  }

  const parts = value.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
  if (!parts) return null;

  const totalSeconds = (Number(parts[1] || 0) * 3600) + (Number(parts[2] || 0) * 60) + Number(parts[3] || 0);
  return totalSeconds > 0 ? totalSeconds : null;
};

const extractVideoIdFromYouTubePath = (parsedUrl) => {
  const hostname = normalizeYouTubeHostname(parsedUrl.hostname);
  if (hostname === "youtu.be") {
    return parsedUrl.pathname.split("/").filter(Boolean)[0] || null;
  }

  if (parsedUrl.pathname === "/watch" || parsedUrl.pathname.endsWith("/watch")) {
    return parsedUrl.searchParams.get("v");
  }

  if (parsedUrl.pathname.startsWith("/embed/")) {
    return parsedUrl.pathname.split("/embed/")[1]?.split("/")[0] || null;
  }

  if (parsedUrl.pathname.startsWith("/shorts/")) {
    return parsedUrl.pathname.split("/shorts/")[1]?.split("/")[0] || null;
  }

  if (parsedUrl.pathname.startsWith("/live/")) {
    return parsedUrl.pathname.split("/live/")[1]?.split("/")[0] || null;
  }

  return parsedUrl.searchParams.get("v");
};

const recoverYouTubeVideoIdFromLookalike = (parsedUrl) => {
  const fromQuery = parsedUrl.searchParams.get("v");
  if (fromQuery) return fromQuery;

  const parts = parsedUrl.pathname.split("/").filter(Boolean);
  if (!parts.length) return null;

  if (["embed", "shorts", "live"].includes(parts[0])) {
    return parts[1] || null;
  }

  if (parts.length === 1) {
    return parts[0];
  }

  return null;
};

const extractVideoIdFromLooseString = (rawUrl) => {
  if (rawUrl.includes("youtu.be/")) {
    return rawUrl.split("youtu.be/")[1]?.split(/[?&]/)[0] || null;
  }

  if (rawUrl.includes("watch?v=")) {
    return rawUrl.split("v=")[1]?.split(/[?&]/)[0] || null;
  }

  if (rawUrl.includes("/embed/")) {
    return rawUrl.split("/embed/")[1]?.split(/[?&]/)[0] || null;
  }

  if (rawUrl.includes("/shorts/")) {
    return rawUrl.split("/shorts/")[1]?.split(/[?&]/)[0] || null;
  }

  return null;
};

export function getYouTubeVideoRequest(url) {
  const rawUrl = String(url || "").trim();
  if (!rawUrl) return null;

  try {
    const parsedUrl = parseUrlMaybeMissingProtocol(rawUrl);
    const hostname = normalizeYouTubeHostname(parsedUrl.hostname);
    const videoId = isOfficialYouTubeHost(hostname)
      ? extractVideoIdFromYouTubePath(parsedUrl)
      : recoverYouTubeVideoIdFromLookalike(parsedUrl);

    if (!isLikelyYouTubeVideoId(videoId)) return null;

    const startSeconds = parseYouTubeStartSeconds(
      parsedUrl.searchParams.get("start") || parsedUrl.searchParams.get("t")
    );

    return startSeconds != null ? { videoId, startSeconds } : { videoId };
  } catch (error) {
    const videoId = extractVideoIdFromLooseString(rawUrl);
    return isLikelyYouTubeVideoId(videoId) ? { videoId } : null;
  }
}

export function extractYoutubeVideoId(url) {
  const request = getYouTubeVideoRequest(url);
  if (request?.videoId) return request.videoId;

  const rawUrl = String(url || "").trim();
  if (!rawUrl) return null;

  try {
    return extractVideoIdFromLooseString(rawUrl) || null;
  } catch (error) {
    console.error("Error extracting YouTube video ID:", error);
    return null;
  }
}

export function buildYouTubeEmbedUrl(url, options = {}) {
  const videoRequest = getYouTubeVideoRequest(url);
  if (!videoRequest?.videoId) return "";

  const origin = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "";

  const showControls = options.showControls === true;
  const playerParams = showControls
    ? "autoplay=1&enablejsapi=1&controls=1&modestbranding=1&rel=0&playsinline=1"
    : "autoplay=1&enablejsapi=1&controls=0&disablekb=1&fs=0&iv_load_policy=3&modestbranding=1&rel=0&loop=0&playsinline=1&cc_load_policy=0&hl=en";
  let embedUrl = `https://www.youtube.com/embed/${videoRequest.videoId}?${playerParams}`;
  if (origin) {
    embedUrl += `&origin=${encodeURIComponent(origin)}`;
  }

  if (videoRequest.startSeconds != null) {
    embedUrl += `&start=${videoRequest.startSeconds}`;
  }

  return embedUrl;
}

export function buildYoutubePreviewEmbedUrl(url) {
  return buildYouTubeEmbedUrl(url);
}

export function buildPreMadePlaylistTrackPreviewKey(playlistId, index) {
  return `${playlistId}:${index}`;
}

export const LONG_YOUTUBE_TRACK_WARNING_SECONDS = 15 * 60;

export function parseYouTubeIso8601Duration(isoDuration) {
  const value = String(isoDuration || "").trim().toUpperCase();
  if (!value) return null;

  const match = value.match(
    /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/,
  );
  if (!match) return null;

  const days = Number(match[1] || 0);
  const hours = Number(match[2] || 0);
  const minutes = Number(match[3] || 0);
  const seconds = Number(match[4] || 0);
  const totalSeconds = (days * 86400) + (hours * 3600) + (minutes * 60) + seconds;

  return Number.isFinite(totalSeconds) ? Math.floor(totalSeconds) : null;
}

export function formatYouTubeDurationLabel(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  const parts = [];

  if (hours) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  if (minutes) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  if (!hours && !minutes) {
    parts.push(`${remainingSeconds} ${remainingSeconds === 1 ? "second" : "seconds"}`);
  }

  return parts.join(" ");
}

export function isLongYouTubeTrack(totalSeconds) {
  return Number.isFinite(totalSeconds) && totalSeconds >= LONG_YOUTUBE_TRACK_WARNING_SECONDS;
}

export function getLongYouTubeTrackWarning(totalSeconds) {
  return `This video is ${formatYouTubeDurationLabel(totalSeconds)} long. Auto Music plays each YouTube video as one song from the start, so mix or "playlist" videos keep restarting the same first songs. Add individual song videos, or use Add YouTube Playlist.`;
}
